import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth, canEditEquipment } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Stack,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  CardActionArea,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterAlt as FilterAltIcon,
  Search as SearchIcon,
  FilterAltOff as FilterAltOffIcon,

} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L, { marker } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import standardMarkerIcon from '../assets/leaflet forge blue marker-icon.png';
import selectedMarkerIcon from '../assets/leaflet selected marker-icon.png';
import shadowIcon from '../assets/leaflet marker shadow.png';

import { fetchProjects, fetchRegions, fetchOffices, fetchTechniques } from '../services/api';

const standardMarker = new L.Icon({
  iconUrl: standardMarkerIcon,
  shadowUrl: shadowIcon,
  iconSize: [25, 41],
  shadowSize: [41, 41],
  iconAnchor: [12, 41],
  shadowAnchor: [12, 41],
  popupAnchor: [1, -34],
  className: 'standardMarkerClass'
});

const selectedMarker = new L.Icon({
  iconUrl: selectedMarkerIcon,
  shadowUrl: shadowIcon,
  iconSize: [30, 49],
  shadowSize: [49, 49],
  iconAnchor: [14, 49],
  shadowAnchor: [14, 49],
  popupAnchor: [1, -41],
  className: 'standardMarkerClass'
})

// misc helpers
const getUniqueValues = (data, field) => [...new Set(data.map((item) => item[field]))]

const getAutocompleteOptions = (data, field) => {
  let uniqueValues = getUniqueValues(data, field);
  let optionValues = uniqueValues.filter(item => item != undefined && item != '');
  optionValues.sort();
  return optionValues;
}

const getEndDateYearOptions = (projects) => {
  let yearOptions = [];
  const pastProjects = projects.filter(pr => pr.status === "Past" && pr.lastStaffTimesheetDate !== undefined);
  if (pastProjects.length > 0) {
    let uniqueYears = [...new Set(pastProjects.map(pr => new Date(pr.lastStaffTimesheetDate).getFullYear()))];
    if (uniqueYears !== undefined) {
      uniqueYears.sort((a, b) => a - b);
      yearOptions = uniqueYears;
    }
  }
  return yearOptions;
}

// page element
function JobSitesPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [projects, setProjects] = useState([]);
  const [applications, setApplications] = useState([]);
  const [regions, setRegions] = useState([]);
  const [offices, setOffices] = useState([]);
  const [subRegions, setSubRegions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(-1);
  const [searchText, setSearchText] = useState('');



  const markerRefs = useRef({});


  const getMarkerIcon = (projectId) => {
    if (projectId === selectedProjectId) {
      return selectedMarker;
    }
    return standardMarker;
  }

  // --------------------
  // load projects data
  // --------------------
  const loadAll = async () => {
    try {
      const [projectsData, applicationsData, regionsData, officesData] = await Promise.all([
        fetchProjects(),
        fetchTechniques(),
        fetchRegions(),
        fetchOffices(),
      ]);
      setApplications(applicationsData.techniques || []);
      setRegions(regionsData.regions || []);
      setOffices(officesData.offices || []);
      setSubRegions([...new Set(offices.filter(office => office.subRegion != undefined && office.subRegion != '')
        .map((office) => office.subRegion))] || []);
      setProjects(projectsData.projects || []);
    } catch (err) {
      console.error('Error loading job-sites page data:', err);
    } finally {
      setError();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);


  // options
  const regionOptions = getAutocompleteOptions(projects, "regionName");
  //const officeOptions = getAutocompleteOptions(offices, "officeName");
  const applicationOptions = getAutocompleteOptions(applications, "techniqueName");
  const statusOptions = ["Current", "Past", "Future"];
  const endDateYearOptions = getEndDateYearOptions(projects);


  const getDefaultRegion = () => {
    const auth = useAuth() || {};
    const user = auth.user || null;

    const region = user.businessUnit;
    if (["Central", "East", "South", "West"].includes(region)) {
      return [`${region}`]
    }
    return [];
  }

  const filtersSessionKey = 'jobSitesFilters';
  const initialFilters = () => {
    try {
      const savedFilters = sessionStorage.getItem(filtersSessionKey);
      if (savedFilters) {
        return JSON.parse(savedFilters);
      }
    } catch (err) {
      console.warn('Unable to read saved job site filters from sessionStorage:', err);
    }
    return {
      region: getDefaultRegion(),
      office: [],
      application: [],
      status: ["Current"],
      endDateYear: [],
    };
  };

  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [draftFilters, setDraftFilters] = useState(appliedFilters);

  useEffect(() => {
    try {
      sessionStorage.setItem(filtersSessionKey, JSON.stringify(appliedFilters));
    } catch (err) {
      console.warn('Unable to save job site filters to sessionStorage:', err);
    }
  }, [appliedFilters]);


  // --------------------
  // filtering logic
  // --------------------
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchRegion =
        appliedFilters.region.length === 0 ||
        appliedFilters.region.includes(project.regionName);

      const matchOffice =
        appliedFilters.office.length === 0 ||
        appliedFilters.office.includes(project.regionalOffice);

      const matchTechnique =
        appliedFilters.application.length === 0 ||
        appliedFilters.application.some(filter => project.applicationNames.includes(filter));

      const matchStatus =
        appliedFilters.status.length === 0 ||
        appliedFilters.status.includes(project.status);

      const matchEndDateYear =
        appliedFilters.endDateYear.length === 0 ||
        project.lastStaffTimesheetDate !== undefined &&
        appliedFilters.endDateYear.includes(new Date(project.lastStaffTimesheetDate).getFullYear());

      const matchSearchText =
        searchText.length === 0 ||
        project.projectName.toLowerCase().includes(searchText.trim().toLowerCase()) ||
        project.projectNumber.toLowerCase().includes(searchText.trim().toLowerCase());

      return matchRegion && matchOffice && matchTechnique && matchStatus && matchEndDateYear && matchSearchText;
    });
  }, [appliedFilters, draftFilters, projects, searchText]);

  //filter office options for dropdown dependency with region
  const filteredOffices = useMemo(() => {
    return offices.filter((office) => {
      const matchOffice = draftFilters.region.length === 0 ||
        draftFilters.region.some(filter => office.regionName.includes(filter));
      return matchOffice;
    });
  }, [appliedFilters, draftFilters, offices]);

  // --------------------
  // handlers
  // --------------------

  const handleOpen = () => {
    setDraftFilters(appliedFilters);
    setOpen(true);
  }

  const handleClose = () => setOpen(false);

  const handleApply = () => {
    setAppliedFilters(draftFilters);
    setOpen(false);
  };

  const updateDraft = (field, value) => {
    setDraftFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRegionChange = (selectedRegions) => {
    setDraftFilters((prev) => ({
      ...prev,
      //clear office selections
      ["office"]: [],
      ["region"]: selectedRegions,
    }));

  }

  const handleEndDateYearChange = (selectedYears) => {
    if (selectedYears.length === 0) {
      updateDraft("endDateYear", selectedYears);
    } else {
      setDraftFilters((prev) => ({
        ...prev,
        //apply filter for past projects by default
        ["status"]: ["Past"],
        ["endDateYear"]: selectedYears,
      }));
    }
  }

  const handleCardMouseOver = (e, projectId) => {
    e.stopPropagation();
    setSelectedProjectId(projectId);
    const marker = markerRefs.current[projectId];
    if (marker) {
      marker.icon = selectedMarker
      marker.openPopup();
    }
  }

  const handleCardMouseLeave = (e, projectId) => {
    e.stopPropagation();
    setSelectedProjectId(-1);
    const marker = markerRefs.current[projectId];
    if (marker) {
      marker.closePopup();
    }
  }

  const defaultMapCenter = [39.8283, -98.5795];


  return (
    <>
      {/* <Button variant='contained' sx={{ fontSize: '12px' }} onClick={handleOpen}>Filter Projects</Button> */}
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 5 }}>
          {/* <Button onClick={() => console.log(endDateYearOptions)}>testValueName</Button>        */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<FilterAltIcon size={20} color="inherit" />}
            onClick={handleOpen}
          >
            {'Filter'}
          </Button>
          {/* <Box sx={{ flexGrow: 1 }} /> */}
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search projects..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                '& fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                '&:hover fieldset': { borderColor: 'primary.dark' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
            }}
          />

        </Stack>
      </Box>

      {/* filter dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h5" fontWeight={700}>Project Filters</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>

          <Grid>
            {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}> */}
            <Stack spacing={1} sx={{ width: { xs: '100%', sm: 500 }, gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: { xs: 88, sm: 120 }, flexShrink: 0 }}>Regions</Typography>
                <Autocomplete
                  size="small"
                  fullWidth
                  multiple
                  id="regions"
                  options={regionOptions}
                  value={draftFilters.region}
                  onChange={(e, value) => handleRegionChange(value)}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                          border: "2px light blue",
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: { xs: 88, sm: 120 }, flexShrink: 0 }}>Offices</Typography>
                <Autocomplete
                  size="small"
                  fullWidth
                  multiple
                  id="offices"
                  options={filteredOffices.map(office => office.sfTerritoryName)}
                  value={draftFilters.office}
                  getOptionLabel={(option) => option}
                  onChange={(e, value) => updateDraft("office", value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                          border: "2px light blue",
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: { xs: 88, sm: 120 }, flexShrink: 0 }}>Applications</Typography>
                <Autocomplete
                  size="small"
                  fullWidth
                  multiple
                  id="applications"
                  options={applicationOptions}
                  value={draftFilters.application}
                  getOptionLabel={(option) => option}
                  onChange={(e, value) => updateDraft("application", value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                          border: "2px light blue",
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: { xs: 88, sm: 120 }, flexShrink: 0 }}>Status</Typography>
                <Autocomplete
                  size="small"
                  fullWidth
                  multiple
                  id="statuses"
                  options={statusOptions}
                  value={draftFilters.status}
                  getOptionLabel={(option) => option}
                  onChange={(e, value) => updateDraft("status", value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                          border: "2px light blue",
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: { xs: 88, sm: 120 }, flexShrink: 0 }}>Completion Years</Typography>
                <Autocomplete
                  size="small"
                  fullWidth
                  multiple
                  id="endDateYears"
                  options={endDateYearOptions}
                  value={draftFilters.endDateYear}
                  getOptionLabel={(option) => option}
                  onChange={(e, value) => handleEndDateYearChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                          border: "2px light blue",
                        },
                      }}
                    />
                  )}
                />
              </Box>

            </Stack>
          </Grid>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>


      <Box
        sx={{
          display: "flex",
          // Phones stack the two panes (map first) and let the page scroll;
          // desktop keeps the side-by-side split at a fixed height.
          flexDirection: { xs: "column", md: "row" },
          height: { xs: "auto", md: "70vh" },
          overflow: { xs: "visible", md: "hidden" },
          p: { xs: 1, md: 2 },
          gap: 2,
        }}
      >

        {/* paper element containing header and table */}
        <Paper
          elevation={3}
          sx={{
            width: { xs: "100%", md: "33%" },
            height: { xs: 420, md: "auto" },
            order: { xs: 2, md: 0 },
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
          }}
        >

          {/* project cards section */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              borderRadius: 2,
              py: 1,
              pl: 1
            }}
          >
            <Stack spacing={1}>
              {filteredProjects.map((project) => (
                <Card
                  key={project.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}

                >
                  <CardActionArea
                    onMouseOver={(e) => handleCardMouseOver(e, project.id)}
                    onMouseLeave={(e) => handleCardMouseLeave(e, project.id)}
                  >

                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>

                      {/* Number + technique pills on one line (pills kept right),
                          then the project name and location on their own lines */}
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body1" fontWeight={600} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {project.projectNumber || '-'}
                          </Typography>
                          <Box sx={{ flexGrow: 1 }} />
                          {project.applicationCodes.length > 0 && (
                            <Box sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                              justifyContent: 'flex-end',
                              flexShrink: 0,
                              maxWidth: { xs: 190, sm: 200 },
                            }}>
                              {project.applicationCodes.map(ac => (
                                <Tooltip key={ac.code} title={ac.name} arrow>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      width: 56,
                                      backgroundColor: ac.iconHex,
                                      borderColor: ac.iconHex,
                                      color: '#fafafa',
                                      '& .MuiChip-label': { fontSize: '0.75rem', px: 0.5 }
                                    }}
                                    label={ac.code || '-'} />
                                </Tooltip>
                              ))}
                            </Box>
                          )}
                        </Stack>

                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{ mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {project.shortName || '-'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {project.cityStateLabel || '-'}
                        </Typography>
                      </Box>

                    </CardContent>

                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          </Box>



        </Paper>

        {/* map section */}
        <Paper
          elevation={3}
          sx={{
            width: { xs: "100%", md: "67%" },
            height: { xs: 320, md: "auto" },
            order: { xs: 1, md: 0 },
            position: "relative",
          }}
        >


          <MapContainer
            center={defaultMapCenter}
            zoom={4}
            style={{
              height: "100%",
              width: "100%",
            }}
          >
            <TileLayer
              attribution='<a href="https://www.jawg.io" target="_blank">&copy; Jawg Maps</a>'
              url={isDark
                ? 'https://tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
                : 'https://tile.jawg.io/jawg-streets/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
              }
            />

            {filteredProjects.map((project) => (
              <Marker
                key={project.id}
                position={[project.latitude, project.longitude]}
                ref={(ref) => {
                  if (ref) markerRefs.current[project.id] = ref;
                }}
                icon={getMarkerIcon(project.id)}
              >
                <Popup autoPan={false}>{project.projectName}</Popup>
              </Marker>
            ))}
          </MapContainer>

        </Paper>

      </Box>
    </>
  );

}

export default JobSitesPage;



